import { z } from 'zod';
import {
  CHANNELS,
  CLAIM_KINDS,
  CLAIM_STATUSES,
  CONTRACT_STATUSES,
  INSTALLMENT_STATUSES,
  LANGUAGES,
  PERIODICITIES,
  RENEWAL_STATUSES,
} from '../domain';
import { isoDateSchema } from './referentials';

// Assurés et contrats (BA-008, spec 03, spec 08 Contrats et assurés).

export const pageQuerySchema = z.object({
  /** Curseur opaque de la page suivante (identifiant de la dernière ligne reçue). */
  cursor: z.string().max(64).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

const addressSchema = z.record(z.string().max(40), z.string().max(200));

// --- Assurés ---

export const policyholderSchema = z.object({
  id: z.string(),
  kind: z.enum(['person', 'company']),
  displayName: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  companyName: z.string().nullable(),
  externalRef: z.string().nullable(),
  phone: z.string().nullable(),
  phoneVerifiedAt: z.string().nullable(),
  email: z.string().nullable(),
  cin: z.string().nullable(),
  taxId: z.string().nullable(),
  preferredChannel: z.enum(CHANNELS),
  language: z.enum(LANGUAGES),
  optOutAt: z.string().nullable(),
  optOutChannels: z.array(z.enum(CHANNELS)),
  address: addressSchema.nullable(),
  createdAt: z.string(),
});
export type PolicyholderDto = z.infer<typeof policyholderSchema>;

export const policyholderPageSchema = z.object({
  items: z.array(policyholderSchema),
  nextCursor: z.string().nullable(),
});

export const policyholderQuerySchema = pageQuerySchema.extend({
  /** Recherche globale : téléphone, CIN ou email (exacts, sur empreinte), sinon nom. */
  q: z.string().trim().min(2).max(120).optional(),
});

const contactFields = {
  phone: z.string().max(40).nullable().optional(),
  email: z.string().max(254).nullable().optional(),
  preferredChannel: z.enum(CHANNELS).optional(),
  language: z.enum(LANGUAGES).optional(),
};

export const policyholderCreateSchema = z
  .object({
    kind: z.enum(['person', 'company']),
    firstName: z.string().trim().max(80).nullable().optional(),
    lastName: z.string().trim().max(80).nullable().optional(),
    companyName: z.string().trim().max(160).nullable().optional(),
    externalRef: z.string().trim().max(64).nullable().optional(),
    cin: z.string().max(20).nullable().optional(),
    taxId: z.string().trim().max(40).nullable().optional(),
    address: addressSchema.nullable().optional(),
    ...contactFields,
  })
  .refine((p) => (p.kind === 'company' ? !!p.companyName : !!p.lastName), {
    message: 'Nom requis (raison sociale pour une entreprise)',
  });

/** Édition limitée au contact et aux préférences (spec 08) ; le reste vient de l'import ou de l'API. */
export const policyholderPatchSchema = z.object(contactFields).partial().strict();

// --- Contrats ---

export const installmentSummarySchema = z.object({
  id: z.string(),
  sequenceNo: z.number().int(),
  periodStart: z.string(),
  periodEnd: z.string(),
  dueDate: z.string(),
  amountDue: z.number().int(),
  amountPaid: z.number().int(),
  status: z.enum(INSTALLMENT_STATUSES),
  source: z.enum(['computed', 'imported', 'api']),
});

export const contractSchema = z.object({
  id: z.string(),
  policyNumber: z.string(),
  externalRef: z.string().nullable(),
  status: z.enum(CONTRACT_STATUSES),
  insurerId: z.string(),
  productLineId: z.string(),
  policyholderId: z.string(),
  agentId: z.string().nullable(),
  effectiveDate: z.string(),
  expiryDate: z.string().nullable(),
  periodicity: z.enum(PERIODICITIES),
  /** Prime annuelle en millimes (ADR-002). */
  premiumAmount: z.number().int(),
  currency: z.literal('TND'),
  insuredObjectLabel: z.string().nullable(),
  autoRenew: z.boolean(),
  renewalNoticeDays: z.number().int().nullable(),
  dueNoticeDays: z.number().int().nullable(),
  scenarioOverrideId: z.string().nullable(),
  createdAt: z.string(),
});

export const contractDetailSchema = contractSchema.extend({
  policyholder: z.object({ id: z.string(), displayName: z.string() }),
  installments: z.array(installmentSummarySchema),
});

export const contractPageSchema = z.object({
  items: z.array(contractSchema),
  nextCursor: z.string().nullable(),
});

export const contractQuerySchema = pageQuerySchema.extend({
  policyNumber: z.string().trim().max(64).optional(),
  policyholderId: z.string().optional(),
  insurerId: z.string().optional(),
  productLineId: z.string().optional(),
  agentId: z.string().optional(),
  status: z.enum(CONTRACT_STATUSES).optional(),
});

const millimes = z.number().int().min(0).max(1_000_000_000_000);

export const contractCreateSchema = z
  .object({
    insurerId: z.string().min(1),
    productLineId: z.string().min(1),
    policyholderId: z.string().min(1),
    agentId: z.string().nullable().optional(),
    policyNumber: z.string().trim().min(1).max(64),
    externalRef: z.string().trim().max(64).nullable().optional(),
    effectiveDate: isoDateSchema,
    expiryDate: isoDateSchema.nullable().optional(),
    periodicity: z.enum(PERIODICITIES).optional(),
    premiumAmount: millimes,
    insuredObjectLabel: z.string().trim().max(120).nullable().optional(),
    autoRenew: z.boolean().optional(),
    renewalNoticeDays: z.number().int().min(0).max(365).nullable().optional(),
    /** Préavis avant chaque échéance propre au contrat (BA-079) ; nul : celui de la branche. */
    dueNoticeDays: z.number().int().min(1).max(365).nullable().optional(),
    scenarioOverrideId: z.string().nullable().optional(),
  })
  .refine((c) => !c.expiryDate || c.expiryDate > c.effectiveDate, {
    message: 'La date d’expiration doit suivre la date d’effet',
    path: ['expiryDate'],
  });

/** Champs modifiables : les échéances futures non verrouillées sont recalculées (ADR-012). */
export const contractPatchSchema = z
  .object({
    agentId: z.string().nullable(),
    externalRef: z.string().trim().max(64).nullable(),
    expiryDate: isoDateSchema.nullable(),
    periodicity: z.enum(PERIODICITIES),
    premiumAmount: millimes,
    insuredObjectLabel: z.string().trim().max(120).nullable(),
    autoRenew: z.boolean(),
    renewalNoticeDays: z.number().int().min(0).max(365).nullable(),
    dueNoticeDays: z.number().int().min(1).max(365).nullable(),
    scenarioOverrideId: z.string().nullable(),
  })
  .partial()
  .strict();

export const contractStatusChangeSchema = z.object({
  status: z.enum(CONTRACT_STATUSES),
  reason: z.string().trim().min(1).max(500),
});

export const contractStatusHistorySchema = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      fromStatus: z.enum(CONTRACT_STATUSES).nullable(),
      toStatus: z.enum(CONTRACT_STATUSES),
      reason: z.string().nullable(),
      changedAt: z.string(),
    }),
  ),
});

// --- Garanties et avenants (BA-069) ---

export const coverageInputSchema = z.object({
  code: z.string().trim().min(1).max(30),
  label: z.string().trim().min(1).max(120),
  sumInsured: millimes.nullable().optional(),
  premiumPart: millimes.nullable().optional(),
});
/** Remplacement de la liste des garanties d'un contrat (30 au plus, codes distincts). */
export const coverageListInputSchema = z.object({
  items: z
    .array(coverageInputSchema)
    .max(30)
    .refine(
      (items) => new Set(items.map((i) => i.code)).size === items.length,
      'Codes de garantie en double',
    ),
});
export const coverageSchema = z.object({
  id: z.string(),
  code: z.string(),
  label: z.string(),
  sumInsured: z.number().int().nullable(),
  premiumPart: z.number().int().nullable(),
});
export const coverageListSchema = z.object({ items: z.array(coverageSchema) });

export const ENDORSEMENT_KINDS = [
  'premium_change',
  'coverage_change',
  'object_change',
  'address_change',
  'other',
] as const;
export type EndorsementKind = (typeof ENDORSEMENT_KINDS)[number];

export const endorsementInputSchema = z.object({
  kind: z.enum(ENDORSEMENT_KINDS),
  effectiveDate: isoDateSchema,
  /** Variation de la prime annuelle en millimes, négative pour une baisse (ADR-012). */
  deltaPremium: z.number().int().min(-1_000_000_000_000).max(1_000_000_000_000).default(0),
  note: z.string().trim().max(500).nullable().optional(),
});
export const endorsementSchema = z.object({
  id: z.string(),
  kind: z.enum(ENDORSEMENT_KINDS),
  effectiveDate: z.string(),
  deltaPremium: z.number().int(),
  note: z.string().nullable(),
  createdAt: z.string(),
});
export const endorsementListSchema = z.object({
  items: z.array(endorsementSchema),
  /** Prime annuelle en vigueur aujourd'hui, avenants compris. */
  currentPremium: z.number().int(),
});

// --- Renouvellement (BA-070) ---

export const renewalOfferInputSchema = z.object({
  /** Prime annuelle proposée pour la nouvelle période, en millimes. */
  premiumAmount: millimes,
  /** Dernier jour pour répondre ; par défaut, la date d'expiration du contrat. */
  validUntil: isoDateSchema.optional(),
});
export const renewalOfferSchema = z.object({
  id: z.string(),
  premiumAmount: z.number().int(),
  startDate: z.string(),
  endDate: z.string(),
  validUntil: z.string(),
  status: z.enum(RENEWAL_STATUSES),
  respondedAt: z.string().nullable(),
  createdAt: z.string(),
});
export const renewalOfferListSchema = z.object({ items: z.array(renewalOfferSchema) });

// --- Déclaration de sinistre (BA-077) ---

export const claimDeclarationInputSchema = z.object({
  kind: z.enum(CLAIM_KINDS),
  occurredOn: isoDateSchema,
  place: z.string().trim().min(1).max(120),
  /** Circonstances, sans donnée de santé (spec 11). */
  description: z.string().trim().max(1000).optional(),
  thirdParty: z.boolean().optional(),
});
export const claimDeclarationSchema = z.object({
  id: z.string(),
  reference: z.string(),
  kind: z.enum(CLAIM_KINDS),
  occurredOn: z.string(),
  place: z.string(),
  description: z.string().nullable(),
  thirdParty: z.boolean(),
  status: z.enum(CLAIM_STATUSES),
  origin: z.enum(['page', 'portal']),
  createdAt: z.string(),
});
export const claimDeclarationListSchema = z.object({ items: z.array(claimDeclarationSchema) });
export const claimStatusPatchSchema = z.object({ status: z.enum(CLAIM_STATUSES) });
